"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { CompanyEntity } from "./CompanyEntity";

interface CompanyEntitySkeletonProps {
  layout?: "card" | "row";
  className?: string;
}

const ShimmerBlock: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn("animate-pulse bg-muted", className)} />
);

/**
 * Loading placeholder matching the card + row layouts. Same
 * borders/paddings/shadow as the real component so pages don't shift
 * on hydrate.
 */
export const CompanyEntitySkeleton: React.FC<CompanyEntitySkeletonProps> = ({
  layout = "card",
  className,
}) => {
  if (layout === "row") {
    return (
      <div
        className={cn(
          "px-3 py-2.5 border-b border-border",
          className,
        )}
        style={{ backgroundColor: "var(--background)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ShimmerBlock className="w-6 h-6" />
            <ShimmerBlock className="h-4 w-40" />
            <ShimmerBlock className="h-3 w-24" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ShimmerBlock className="h-7 w-20" />
            <ShimmerBlock className="h-7 w-28" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "h-full bg-card border border-border p-4 space-y-3",
        className,
      )}
      style={{ boxShadow: "var(--shadow-warm-sm)" }}
    >
      <div className="flex items-start gap-3">
        <ShimmerBlock className="w-12 h-12 rounded-xl" />
        <div className="flex-1 space-y-2">
          <ShimmerBlock className="h-4 w-2/3" />
          <ShimmerBlock className="h-3 w-1/2" />
        </div>
      </div>
      <ShimmerBlock className="h-3 w-full" />
      <ShimmerBlock className="h-3 w-5/6" />
      <div className="pt-2 border-t border-border/40 space-y-2">
        <ShimmerBlock className="h-3 w-1/3" />
        <ShimmerBlock className="h-3 w-1/2" />
        <ShimmerBlock className="h-3 w-2/5" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <ShimmerBlock className="h-8 w-24" />
        <ShimmerBlock className="h-8 w-28" />
      </div>
    </div>
  );
};

CompanyEntity.Skeleton = CompanyEntitySkeleton;
